/* ── 204 §1 [RULED — Laurent, 2026-09-22] — A MODAL OPENED WHILE A FULL-SCREEN
 * PANE IS OPEN RENDERS ABOVE THAT PANE, AND THE PANE STAYS OPEN BEHIND IT.
 *
 * THE DEFECT (Laurent's eye, in Safari — the one browser that fires a drag out
 * of Outlook; Chromium never does and WebKit cannot launch on staging, so no
 * drive reproduces it and none claims to): My Bento or the Eisenhower matrix
 * open — a `position: fixed` pane at `Z.pane` (9999; BentoDialog.tsx portalled
 * to body, EisenhowerDialog.tsx in App's tree) — and an email dropped on it.
 * The drop layer's paint sits one rung UNDER the pane (`Z.paneBackdrop`, 9998),
 * and the modal the drop opens is an ordinary `ModalLayout`, whose overlay is
 * `.modal-overlay` at `Z.modalOverlay` (100). The modal IS created and IS
 * buried: what shows is its overlay's `backdrop-filter: blur(3px)` on the
 * header, which the pane does not cover. CENSUSED (§1.2): the drop is NOT the
 * only one — everything the HEADER still offers over a pane lands under it
 * the same way: the timer's stop (`openTimerLogTe` → the time-entry modal),
 * the sign-out confirm (`confirmAndUndo`, default `Z.modal2` 300), the
 * hotkeys' Alt+N / Alt+T (hooks/popoverChrome.ts), the chooser's own three
 * follow-ups (the task, event and time-entry modals). So the fix is the RULE,
 * in the one shell they all wear, never a patch on the drop.
 *
 * THE RULE, the drawers' own convention (`DRAWER_Z + 2` for the menus a drawer
 * raises, `DRAWER_Z + 4` for its dialogs — MessagesDrawer, EmailDeepSearchDrawer):
 * while a pane is open, `ModalLayout` takes `Z.paneModal` (pane + 2) for a
 * base dialog and `Z.paneModal2` (pane + 4) for one a mount stacks over it
 * (any figure above `Z.modalOverlay`), and the PORTALLED PICKERS such a modal
 * hosts (MenuSelect, TypeaheadPicker, FieldSelect, TimeField — their menus
 * are portalled to body at 201 / 1000 and would otherwise paint under the
 * lifted overlay) sit one over it, computed as DocumentEntryModal computes
 * its own (`zIndex + 1`). At the ordinary scale nothing here is read: both
 * contexts are null/false and every figure is what it was.
 *
 * Two contexts, because the two questions have two askers: App knows whether
 * a pane is open (`headerOverlay`, one owner); the shell knows the level it
 * was lifted to, and only its own children need it.
 */
import { createContext, useContext, useEffect } from "react";
import { Z } from "./tokens";

/** true while a full-screen pane (My Bento, the Eisenhower matrix — App's `headerOverlay`) is open. */
export const PaneOpenContext = createContext<boolean>(false);

/** the overlay level of the nearest LIFTED modal shell — null under the ordinary scale. */
export const LiftedLevelContext = createContext<number | null>(null);

/** The modal shell's rung over an open pane: `pane + 2` for a base dialog, `pane + 4` for one a
 *  mount stacks over it (any figure above `modalOverlay` — 300, 400, a drawer's `+ 4`). */
export function paneRungFor(zIndex: number | null | undefined): number {
  return zIndex != null && zIndex > Z.modalOverlay ? Z.paneModal2 : Z.paneModal;
}

/** A portalled picker's menu level: one over the lifted shell it sits in (the tree's `zIndex + 1`
 *  idiom), or its own figure when nothing is lifted — or when its own figure is already higher.
 *
 *  205 §6 — AND A PICKER THAT IS NOT IN A SHELL AT ALL. The §6 census found
 *  « Viewing as » to be an ordinary `MenuSelect` sitting in the header: it calls
 *  this, but `LiftedLevelContext` is null outside a lifted `ModalLayout`, so its
 *  menu stayed at `Z.popover` (201) and painted UNDER the pane's 9999. The same
 *  is true of any picker INSIDE a pane. `Z.picker` (10000) is not a new rung and
 *  not a new idea — it is the one the Bento template picker already takes for
 *  exactly this reason ("the template picker, in the icon's row, right of it,
 *  while the Bento is open (over the 9999 overlay)", App.tsx) — and it sits
 *  UNDER `Z.paneModal` (10001), so a modal over a pane still beats a menu over a
 *  pane. With no pane open both contexts are inert and every figure is what it
 *  was. */
export function useMenuLevel(own: number): number {
  const lifted = useContext(LiftedLevelContext);
  const paneOpen = useContext(PaneOpenContext);
  if (lifted !== null) return Math.max(own, lifted + 1);
  return paneOpen ? Math.max(own, Z.picker) : own;
}

/* ── 205 §5 — AN ESCAPE MEANT FOR A DIALOG MUST NOT ALSO CLOSE THE PANE ──────
 *
 * THE MECHANISM, MEASURED BEFORE ANYTHING CHANGED. Three kinds of Escape
 * handler exist in this tree and they are not peers:
 *
 *  1. THE CHAIN — `useAppHotkeys` (hooks/popoverChrome.ts:30) — ONE `window`
 *     keydown whose body is an ORDERED if-ladder that `return`s after each arm:
 *     searchDlg → discardDlg → sideFly → subtaskDoneAsk → tkCompletePend →
 *     notifOpen → activeModal → mailDlg → doodleDlg → dailyOverview → gSearchQ.
 *     That ladder IS "the topmost thing that can close consumes it" — for the
 *     eleven things it knows about.
 *  2. `ModalLayout`'s opt-in `escToClose` (SharedUI.tsx:1369) — a `window`
 *     keydown that calls `stopPropagation()`.
 *  3. THE TWO PANES' OWN — App.tsx's Eisenhower effect and
 *     BentoDialog.tsx:103-108 — both on `document`, both unconditional while
 *     open, and neither in the chain.
 *
 * AND THE ORDER IS DECIDED BY THE DOM, NOT BY THE LADDER. A keydown bubbles to
 * `document` BEFORE `window`, so the two pane listeners fire FIRST, close the
 * pane, and only then does the chain run and close the dialog. Both close, on
 * one press. `escToClose`'s `stopPropagation()` cannot help: it runs later, and
 * `stopPropagation` does not stop a listener that already ran. That is the
 * whole defect — it is a phase ordering, not a missing condition.
 *
 * THE CURE IS THE ORDER 204 §1 ALREADY BUILT. A pane does not consume an
 * Escape while something is PAINTED ABOVE IT, and the tree already knows
 * exactly when that is: `ModalLayout` lifts itself to `Z.paneModal`/`paneModal2`
 * while `PaneOpenContext` is true. So each lifted surface registers here for as
 * long as it is mounted, and a pane's own listener asks this count first.
 *
 * WHO REGISTERS, and it is not "everything 205 §6 lifts" — it is everything
 * lifted THAT CLOSES ON ESCAPE, which was measured one by one:
 *  · `ModalLayout` — yes. Every dialog over a pane, the chain's `activeModal`
 *    arm and the seven `escToClose` mounts alike.
 *  · `SearchPopover` (Alt+F) — yes. The chain's FIRST arm closes it.
 *  · « Viewing as » and every other `MenuSelect` — NO, AND IT NEEDS NONE.
 *    Its own Escape calls `stopPropagation()` on the trigger (MenuSelect.tsx:
 *    "the menu eats its own Escape"), and React delegates keydown at the ROOT
 *    CONTAINER — a DESCENDANT of `document`. MEASURED in Chromium: with a
 *    listener on `#root` calling `stopPropagation`, `document` never sees the
 *    key at all, so the pane's listener does not run and there is nothing to
 *    stand down from.
 *  · the timer's type dropdown — NOW YES, AND THE ORDER IS THE POINT. 205 §5
 *    refused it and was right to: it had NO Escape handler at all (it closed
 *    on an outside mousedown and nothing else), so registering it would have
 *    made Escape do NOTHING while it was open — worse than closing the pane
 *    behind it. A surface cannot own a key it does not answer.
 *    206 §8.4 gave it the close FIRST (a `document` keydown beside its own
 *    outside-mousedown effect, App.tsx) and the registration second, in that
 *    order and in the same commit, because they are one fact: it is open, it
 *    answers Escape, therefore it owns Escape. On `document` and not `window`
 *    for the reason measured above — a keydown reaches `document` first, so a
 *    `window` listener could never have beaten the panes' own; the registry is
 *    what makes them stand down instead.
 *
 * A MODULE COUNTER AND NOT STATE, deliberately: the pane reads it at the
 * instant the key is pressed, so no re-render is needed, and a re-render would
 * be WRONG — the count changes in an effect, after the paint that made the
 * thing visible. It is also why this lives beside the contexts rather than
 * inside one: a context would make every pane re-render when a dialog opens.
 */
let abovePaneCount = 0;

/** Register a surface as painted ABOVE an open pane for as long as `active` holds.
 *  Called by the lifted shells themselves — `ModalLayout` (SharedUI) and the header
 *  layers 205 §6 lifts — never by a pane. */
export function useAbovePane(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    abovePaneCount += 1;
    return () => {
      abovePaneCount -= 1;
    };
  }, [active]);
}

/** True while anything is painted above the open pane. A pane's Escape listener
 *  reads this AT THE KEYPRESS and stands down — the thing in front owns the key. */
export function isAbovePane(): boolean {
  return abovePaneCount > 0;
}
